import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { BRANCH_AND_RO_LIST_BY_BDM } from "../../constants";
import axios from "axios";

const TaskTable = ({ tasks, setTaskSections }) => {
  const [openPostponeModal, setOpenPostponeModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [selectedOption, setSelectedOption] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [postponeRemark, setPostponeRemark] = useState("");
  const [expandedRemarks, setExpandedRemarks] = useState({}); // Track which remarks are expanded
  const [branch, setBranch] = useState("");
  const [regionalOffice, setRegionalOffice] = useState("");
  const [branchList, setBranchList] = useState([]);
  const [roList, setRoList] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [selectedtaskLocation, setSelectedtaskLocation] = useState("");
  const [selectedTaskLocation, setSelectedTaskLocation] = useState("");
  const [formErrors, setFormErrors] = useState({});

  const getBOandROListByBdm = async () => {
    try {
      setBranchLoading(true);
      const response = await axios.get(
        `${BRANCH_AND_RO_LIST_BY_BDM}/${localStorage.getItem("username")}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      const { regions } = response.data;

      // Flatten branches from all regions
      const branches = regions.flatMap((region) =>
        region.branches.map((b) => ({
          label: b.Branch,
          branchCode: b.BranchCode, // keep if needed for future use
        }))
      );

      // Flatten and remove duplicate RO names
      const roSet = new Set();
      const roNames = [];

      regions.forEach((region) => {
        region.branches.forEach((b) => {
          if (b.RO && !roSet.has(b.RO)) {
            roSet.add(b.RO);
            roNames.push({ label: b.RO, roCode: b.RO });
          }
        });
      });

      setBranchList(branches);
      setRoList(roNames);

      setBranchLoading(false);
    } catch (error) {
      console.error("Error fetching customer:", error);
      setBranchLoading(false);
    }
  };

  const handlePostpone = (taskId, newDate, remark) => {
    const updatedTasks = tasks.map((task) =>
      task.id === taskId
        ? {
            ...task,
            postponedDate: newDate,
            postponedRemark: remark,
            disabled: true,
          }
        : task
    );

    setTaskSections(updatedTasks);
  };

  const handleConfirm = (taskId, option) => {
    const updatedTasks = tasks.map((task) => {
      if (task.id === taskId) {
        const updatedTask = {
          ...task,
          confirmed: true,
          bdm_remark: option,
          disabled: true,
        };

        if (option === "Meeting_Office") {
          updatedTask.selectedTaskLocation = selectedTaskLocation;
          updatedTask.branchOffice =
            selectedTaskLocation === "bo" ? branch : null;
          updatedTask.regionalOffice =
            selectedTaskLocation === "ro" ? regionalOffice : null;
        }

        return updatedTask;
      }
      return task;
    });

    const tasksToSave = updatedTasks.filter((task) => task.confirmed === true);
    setTaskSections(updatedTasks);
  };

  const today = dayjs();

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate && newDate.isValid() ? newDate : null);
  };

  const handleConfirmPostpone = () => {
    if (currentTaskId !== null && selectedDate && postponeRemark) {
      handlePostpone(currentTaskId, selectedDate.toDate(), postponeRemark); // Pass the postpone remark
      setOpenPostponeModal(false);
      setSelectedDate(null);
      setPostponeRemark("");
    } else {
      alert("Please select a valid date and enter a remark.");
    }
  };

  const handleConfirmSubmit = () => {
    console.log("currentTaskId", currentTaskId);
    console.log("selectedOption", selectedOption);

    // Check if "Meeting_office" is selected and perform validation
    if (selectedOption === "Meeting_Office") {
      const errors = {};

      if (!selectedTaskLocation?.trim()) {
        errors.selectedTaskLocation = "Task location is required.";
      }

      if (selectedTaskLocation === "bo" && !branch?.trim()) {
        errors.branch = "Branch Office is required.";
      }

      if (selectedTaskLocation === "ro" && !regionalOffice?.trim()) {
        errors.regionalOffice = "Regional Office is required.";
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors); // assumes you're managing errors in state
        return;
      }
    }

    if (currentTaskId !== null && selectedOption) {
      handleConfirm(currentTaskId, selectedOption);
      setOpenConfirmModal(false);
      setSelectedOption("");
      setSelectedTaskLocation("");
      setFormErrors({});
      setBranch("");
      setBranchList([]);
      setRegionalOffice("");
      setRoList([]);
    } else {
      alert("Please select an option.");
    }
  };

  const toggleRow = (taskId) => {
    setExpandedRow(expandedRow === taskId ? null : taskId);
  };

  const toggleRemarkExpansion = (taskId) => {
    setExpandedRemarks((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const renderRemark = (remark, taskId) => {
    const isValidRemark = typeof remark === "string";
    const words = isValidRemark ? remark.split(" ") : [];
    const isExpanded = expandedRemarks[taskId];

    if (words.length <= 5) {
      return <p>{isValidRemark ? remark : "No remark available."}</p>;
    } else {
      return (
        <>
          <p>
            {isExpanded ? remark : words.slice(0, 5).join(" ") + "..."}
            <Button
              onClick={() => toggleRemarkExpansion(taskId)}
              color="primary"
            >
              {isExpanded ? "See Less" : "See More"}
            </Button>
          </p>
        </>
      );
    }
  };

  return (
    <div className="w-full bg-white shadow-lg rounded-lg overflow-hidden">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-green-500 text-white font-semibold">
            <th className="p-2 text-left">S No.</th>
            <th className="p-2 text-left">Customer Name</th>
            <th className="p-1 text-left">Confirm</th>
            <th className="p-1 text-left">Replan</th>
          </tr>
        </thead>
        <tbody>
          {tasks.length === 0 ? (
            <tr>
              <td colSpan="4" className="p-4 text-center text-gray-500">
                No Data Available Yet.
              </td>
            </tr>
          ) : (
            tasks.map((task, index) => (
              <React.Fragment key={task.id}>
                <tr
                  className={`hover:bg-gray-100 transition ${
                    task.disabled ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  onClick={() => toggleRow(task.id)}
                >
                  <td className="p-2 flex items-center">
                    {index + 1}
                    <ExpandMoreIcon
                      className={`ml-2 cursor-pointer transition-transform ${
                        expandedRow === task.id ? "rotate-180" : ""
                      }`}
                    />
                  </td>
                  <td className="p-1">{task.CustomerName}</td>
                  <td className="p-1" align="center">
                    <IconButton
                      onClick={() => {
                        setCurrentTaskId(task.id);
                        setOpenConfirmModal(true);
                      }}
                      disabled={task.disabled}
                      sx={{
                        backgroundColor: task.disabled ? "#e0e0e0" : "green",
                        color: "white",
                        width: 36,
                        height: 36,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        "&:hover": {
                          backgroundColor: task.disabled
                            ? "#e0e0e0"
                            : "#006400",
                        },
                      }}
                    >
                      <CheckIcon />
                    </IconButton>
                  </td>
                  <td className="p-1" align="center">
                    {task.postponedDate ? (
                      <Typography variant="body2" color="primary">
                        {new Date(task.postponedDate).toLocaleDateString()} -{" "}
                        {task.postponedRemark}
                      </Typography>
                    ) : (
                      <IconButton
                        onClick={() => {
                          setOpenPostponeModal(true);
                          setCurrentTaskId(task.id);
                        }}
                        disabled={task.disabled}
                        sx={{
                          backgroundColor: task.disabled ? "#e0e0e0" : "red",
                          color: "white",
                          width: 36,
                          height: 36,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          "&:hover": {
                            backgroundColor: task.disabled
                              ? "#e0e0e0"
                              : "#D22B2B",
                          },
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                  </td>
                </tr>
                {expandedRow === task.id && (
                  <tr>
                    <td colSpan="4" className="p-4 bg-gray-50">
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>Mobile No:</strong> {task.MobileNo}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>Location:</strong> {task.location}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>Category:</strong> {task.category}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>Sub Category:</strong> {task.sub_category}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Agent Remark:</strong>{" "}
                            {renderRemark(task.agent_remark, task.id)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>BDM Remark:</strong>{" "}
                            {renderRemark(task.bdm_remark, task.id)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>

      {/* Postpone Modal */}
      <Dialog
        open={openPostponeModal}
        onClose={() => setOpenPostponeModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reschedule Task</DialogTitle>
        <DialogContent>
          <DatePicker
            label="New Date"
            value={selectedDate}
            onChange={handleDateChange}
            minDate={today}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
          <TextField
            label="Remarks"
            value={postponeRemark}
            onChange={(e) => setPostponeRemark(e.target.value)}
            fullWidth
            margin="normal"
            error={!postponeRemark && selectedDate !== null}
            helperText={
              !postponeRemark && selectedDate !== null
                ? "Remarks are required."
                : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPostponeModal(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPostpone}
            color="primary"
            variant="contained"
            disabled={!selectedDate || !postponeRemark}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Modal */}
      <Dialog
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Task</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Select Task</InputLabel>

                <Select
                  value={selectedOption}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  label="Select Option"
                >
                  <MenuItem value="">Select an option</MenuItem>
                  <MenuItem value="On Call Discussion">
                    On Call Discussion
                  </MenuItem>
                  {/* <MenuItem value="Site Visit">Site Visit</MenuItem> */}
                  <MenuItem value="Meeting_Office">Meeting_Office</MenuItem>
                  <MenuItem value="Meeting_Field">Meeting_Field</MenuItem>
                  {/* <MenuItem value="Estimation Request">Estimation Request</MenuItem> */}
                </Select>
              </FormControl>
            </Grid>

            {selectedOption === "Meeting_Office" && (
              <>
              <Grid item xs={12}>
                <TextField
                  value={selectedTaskLocation}
                  onChange={(e) => {
                    console.log(e.target.value);

                    setSelectedTaskLocation(e.target.value);
                    setFormErrors((prev) => ({ ...prev, selectedTaskLocation: "" }));
                  }}
                  label="Select Location"
                  fullWidth
                  select
                  required
                  error={!!formErrors.selectedTaskLocation}
                  helperText={formErrors.selectedTaskLocation}
                >
                  <MenuItem value={"ho"}>Head Office</MenuItem>
                  <MenuItem value={"ro"}>Regional Office</MenuItem>
                  <MenuItem value={"bo"}>Branch Office</MenuItem>
                </TextField>
              </Grid>
            

            {selectedTaskLocation === "bo" && (
              <Grid item xs={12}>
                <Autocomplete
                  options={branchList}
                  getOptionLabel={(option) => option.label}
                  value={branchList.find((opt) => opt.label === branch) || null}
                  onOpen={() => {
                    if (!branchList.length) {
                      getBOandROListByBdm(); // Fetch only if list is empty
                    }
                  }}
                  onChange={(event, newValue) => {
                    setBranch(newValue?.label || "");
                    setFormErrors((prev) => ({ ...prev, branch: "" }));
                  }}
                  loading={branchLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Branch Name"
                      fullWidth
                      required
                      error={!!formErrors.branch}
                      helperText={formErrors.branch}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {branchLoading && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}

            {selectedTaskLocation === "ro" && (
              <Grid item xs={12}>
                <Autocomplete
                  options={roList}
                  getOptionLabel={(option) => option.label}
                  value={
                    roList.find((opt) => opt.label === regionalOffice) || null
                  }
                  onOpen={() => {
                    if (!roList.length) {
                      getBOandROListByBdm(); // Fetch only if list is empty
                    }
                  }}
                  onChange={(event, newValue) => {
                    setRegionalOffice(newValue?.label || "");
                    setFormErrors((prev) => ({ ...prev, branch: "" }));
                  }}
                  loading={branchLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select RO Name"
                      fullWidth
                      required
                      error={!!formErrors.regionalOffice}
                      helperText={formErrors.regionalOffice}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {branchLoading && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}
            </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() =>{ 
            setOpenConfirmModal(false);
            setSelectedOption("");
      setSelectedTaskLocation("");
      setFormErrors({});
      setBranch("");
      setBranchList([]);
      setRegionalOffice("");
      setRoList([]);
            }} 
            color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            color="primary"
            variant="contained"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TaskTable;
